import Reveal from '@/components/base/Reveal';

const steps = [
  {
    index: '01',
    stage: 'STRUCTURE',
    title: 'A Structure Is In Place',
    body: 'A clear directional structure or a defined range boundary has to exist first. Until one does, nothing else is evaluated.',
  },
  {
    index: '02',
    stage: 'LEVEL',
    title: 'Price Comes To A Level',
    body: 'The system waits for price to reach a level that matters — a prior high, a prior low, or the edge of a range. It does not chase a move that is already stretched.',
  },
  {
    index: '03',
    stage: 'REACTION',
    title: 'The Level Is Tested',
    body: 'The reaction at that level is what the system reads: did it hold and turn, or break and continue? The type of setup follows the reaction, not a prediction.',
  },
  {
    index: '04',
    stage: 'CONFIRM',
    title: 'The Conditions Align',
    body: 'Only when structure, level and reaction all agree does the setup become valid. Then — and only then — is a direction chosen and an order prepared.',
  },
];

function EntryLogic() {
  return (
    <section
      id="entry-conditions"
      className="w-full scroll-mt-[124px] px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1200px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
              STRATEGY
            </span>
            <span className="w-8 h-px bg-primary-400"></span>
            <span className="text-[11px] font-data tracking-[0.2em] text-foreground-400">
              ENTRY CONDITIONS
            </span>
          </div>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[42px] lg:text-[46px] max-w-[720px]">
            What Counts As A Gold Setup.
          </h2>
          <p className="mt-5 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[620px]">
            A valid trade is not a prediction. It is a small set of conditions that has to line up,
            in order. If any one of them is missing, there is no trade.
          </p>
        </Reveal>

        <Reveal delay={120} className="mt-12">
          <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-5 h-12 border-b border-background-200 bg-background-100">
              <span className="text-[12px] font-data tracking-widest text-foreground-700">
                SETUP CHECKLIST
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
                <span className="text-[10px] font-data tracking-widest text-primary-600">
                  SEQUENTIAL
                </span>
              </span>
            </div>

            <div className="divide-y divide-background-200">
              {steps.map((step) => (
                <div
                  key={step.index}
                  className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 px-4 md:px-5 py-5"
                >
                  <div className="flex items-center gap-3 sm:w-[200px] sm:shrink-0">
                    <span className="w-8 h-8 rounded-full border border-background-300 bg-background-100 flex items-center justify-center shrink-0 font-data text-[12px] text-foreground-600">
                      {step.index}
                    </span>
                    <span className="text-[12px] font-data tracking-[0.16em] text-foreground-950">
                      {step.stage}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold text-foreground-950 text-[16px]">
                      {step.title}
                    </p>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-foreground-600">
                      {step.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 border-t border-background-200 bg-background-100 px-4 md:px-5 py-3">
              <span className="text-[11px] font-data tracking-[0.16em] text-foreground-400">
                NO SETUP · NO TRADE
              </span>
              <span className="text-[11px] font-data tracking-[0.16em] text-foreground-500">
                DOING NOTHING IS A VALID OUTPUT
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={180}>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-5">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-focus-3-line text-[17px] text-primary-700"></i>
                </span>
                <p className="font-heading font-bold text-foreground-950 text-[15px]">
                  Where it enters
                </p>
              </div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-foreground-600">
                Entries are taken at the level, on the confirmation — not at the tip of an extended
                move that has already travelled.
              </p>
            </div>
            <div className="rounded-lg border border-secondary-200 bg-secondary-100/70 px-5 py-5">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-200 flex items-center justify-center shrink-0">
                  <i className="ri-forbid-2-line text-[17px] text-secondary-900"></i>
                </span>
                <p className="font-heading font-bold text-foreground-950 text-[15px]">
                  What it refuses
                </p>
              </div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-foreground-600">
                Chasing a stretched move, trading the middle of a range, or opening a position purely
                because the price happened to move.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default EntryLogic;